import React, {SyntheticEvent, useCallback, useRef} from "react"
import Typography from "@mui/material/Typography"
import {formatTextTestIds} from "@next/utils"
import {
    StyledAccordion,
    StyledAccordionSummary,
    StyledAccordionDetails,
    ExpansionTitle,
    ClearLink,
    FieldSet,
    StyledTypography,
    Legend,
} from "./components"
import env from "../../config/env"
import Facets from "../facets"
import connect from "./connect"
import {FilterType} from "../../config/constants"
import FacetsPrice from "../facetsPrice"
import {TextModel} from "../../models/Text"
import {useCustomTabOrder} from "../../hooks/useCustomTabOrder"

interface FilterProps {
    name: string
    displayName: string
    type: FilterType
    isOpen: boolean
    isFilterSelected?: boolean
    toggleAccordionAction: ({
        event,
        name,
        displayName,
    }: {
        event: SyntheticEvent<Element, Event>
        name: string
        displayName: string
    }) => void
    clearTypeFilters: ({name, displayName}: {name: string; displayName: string}) => void
    text: TextModel
}

export const Filter = ({
    name,
    displayName,
    isOpen,
    toggleAccordionAction,
    clearTypeFilters,
    isFilterSelected,
    type,
    text,
}: FilterProps) => {
    const clearTypeFiltersFn = event => {
        event.stopPropagation()
        clearTypeFilters({name, displayName})
    }

    const expandRef = useRef<HTMLDivElement>(null)
    const clearRef = useRef<HTMLButtonElement>(null)
    const firstOptionRef = useRef<HTMLInputElement>(null)

    useCustomTabOrder({
        elementOrder: [expandRef, clearRef, firstOptionRef],
    })

    const onAccordionChange = useCallback(
        event => {
            toggleAccordionAction({event, name, displayName})
        },
        [toggleAccordionAction, name, displayName],
    )

    return (
        <FieldSet>
            <StyledAccordion square expanded={isOpen} onChange={onAccordionChange}>
                <StyledAccordionSummary
                    expandIcon={
                        <img
                            src={`${env.REACT_APP_BLOB_STORAGE_PATH}/icons/shared/chevron.svg`}
                            alt={text.assetsAlt.chevron}
                        />
                    }
                    aria-controls={`plp-f-c-${name}`}
                    aria-label={displayName}
                    id={`plp-f-h-${name}`}
                    data-testid={formatTextTestIds(`plp-f-f-${name}`)}
                    ref={expandRef}
                >
                    <ExpansionTitle>
                        <Typography variant="h5">
                            <Legend title={displayName} isFilterSelected={isFilterSelected}>
                                {displayName}
                            </Legend>
                        </Typography>
                    </ExpansionTitle>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                    {type === "filter" && (
                        <Facets facetName={name} firstItemRef={firstOptionRef} filterName={displayName} />
                    )}
                    {type === "price" && <FacetsPrice name={name} />}
                </StyledAccordionDetails>
            </StyledAccordion>
            {isFilterSelected && (
                <ClearLink
                    data-testid={formatTextTestIds(`plp-filter-clear-link-${name}`)}
                    onClick={clearTypeFiltersFn}
                    aria-label={text.pages.filters.clearText}
                    ref={clearRef}
                >
                    <StyledTypography variant="h5" component="p">
                        {text.pages.filters.clearText}
                    </StyledTypography>
                </ClearLink>
            )}
        </FieldSet>
    )
}

export default connect(Filter)
